import {
  createPlayerEmbedBridge,
  createPlayerMessage,
  parsePlayerEmbedBootstrap,
  type EmbedHostMessage,
  type EmbedPlayerEventType,
  type EmbedPlayerPayloadMap,
} from '@retro-arcade/embed';
import {
  BrowserEmulatorJsRuntimeHost,
  EmulatorJsAdapter,
  defaultGbaInputProfile,
  emulatorJsGamepadBindings,
  emulatorJsGbaRuntime,
  emulatorJsKeyboardKeys,
  gbaInputActions,
  type EmulatorCapabilities,
  type EmulatorJsGamepadBinding,
  type EmulatorJsKeyboardKey,
  type EmulatorRuntimeSnapshot,
  type GbaInputAction,
  type GbaInputProfile,
} from '@retro-arcade/emulator';
import { useEffect, useRef, useState } from 'react';

import { playerAppMeta } from './app-meta';
import { handlePlayerEmbedCommand } from './embed-controller';
import { resolveEmbedCatalogGame } from './embed-surface';
import {
  clearRomCache,
  inspectRomCache,
  loadRomBinary,
  type RomCacheSummary,
} from './binary-cache';
import {
  PlayerPersistenceStore,
  type CheatEntry,
  type PersistenceIdentity,
  type PlayerStorageSummary,
  type StoredNativeSave,
  type StoredSaveState,
} from './persistence-store';
import './design-system.css';
import './styles.css';
import './runtime.css';

interface CatalogGame {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  gameType: string;
  platform: 'gba';
  releaseDate: string | null;
  developer: string | null;
  publisher: string | null;
  publishedAt: string;
  variant: {
    id: string;
    sizeBytes: number;
    sha256: string;
    region: string | null;
    revision: string | null;
    runtimeProfileId: string;
  };
  runtime: {
    runtimeId: string;
    adapter: string;
    core: string;
    emulatorVersion: string | null;
    threads: boolean;
  };
}

interface LaunchDescriptor {
  access: 'PUBLIC' | 'PRIVATE_TEST';
  catalogRevision: number;
  game: {
    id: string;
    slug: string;
    title: string;
    platform: 'gba';
  };
  variant: {
    id: string;
    sizeBytes: number;
    sha256: string;
    runtimeProfileId: string;
  };
  runtime: {
    runtimeId: string;
    adapter: string;
    core: string;
    emulatorVersion: string | null;
    threads: boolean;
  };
  binary: {
    url: string;
    method: 'GET';
    expiresAt: string;
  };
}

interface ApiErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
  };
}

interface BrowserStorageEstimate {
  readonly usageBytes: number | null;
  readonly quotaBytes: number | null;
}

const initialRuntimeSnapshot: EmulatorRuntimeSnapshot = {
  state: 'idle',
  identity: null,
  error: null,
  volume: 1,
  sequence: 0,
};

const localApiBase = 'http://127.0.0.1:8787';
const configuredApiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(
  /\/$/,
  '',
);
const apiBase = configuredApiBase || localApiBase;
const persistenceStore = new PlayerPersistenceStore();

function cloneGbaInputProfile(profile: GbaInputProfile): GbaInputProfile {
  return {
    keyboard: { ...profile.keyboard },
    gamepad: { ...profile.gamepad },
  };
}

function inputBindingLabel(value: string): string {
  return value
    .replaceAll('_', ' ')
    .replace('BUTTON ', 'Button ')
    .replace('DPAD ', 'D-pad ')
    .replace('LEFT TOP SHOULDER', 'Left shoulder')
    .replace('RIGHT TOP SHOULDER', 'Right shoulder')
    .replace('LEFT BOTTOM SHOULDER', 'Left trigger')
    .replace('RIGHT BOTTOM SHOULDER', 'Right trigger');
}

function persistenceIdentityForLaunch(descriptor: LaunchDescriptor): PersistenceIdentity {
  return {
    gameId: descriptor.game.id,
    variantId: descriptor.variant.id,
    runtimeId: descriptor.runtime.runtimeId,
    romSha256: descriptor.variant.sha256,
  };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MiB`;
}

function formatRomCacheSummary(summary: RomCacheSummary | null): string {
  if (!summary) return 'Not inspected';
  return `${summary.entryCount} item(s) · ${formatBytes(summary.totalBytes)}`;
}

function formatStorageBucket(
  summary: { readonly count: number; readonly totalBytes: number } | undefined,
): string {
  if (!summary) return 'Not inspected';
  return `${summary.count} item(s) · ${formatBytes(summary.totalBytes)}`;
}

function formatBrowserStorageEstimate(estimate: BrowserStorageEstimate | null): string {
  if (!estimate || estimate.usageBytes === null) return 'Unavailable';
  const usage = formatBytes(estimate.usageBytes);
  if (estimate.quotaBytes === null) return usage;
  return `${usage} / ${formatBytes(estimate.quotaBytes)} quota`;
}

async function apiFailure(response: Response): Promise<Error> {
  try {
    const payload = (await response.json()) as ApiErrorEnvelope;
    return new Error(
      `${payload.error?.code ?? `HTTP_${response.status}`}: ${payload.error?.message ?? response.statusText}`,
    );
  } catch {
    return new Error(`HTTP_${response.status}: ${response.statusText}`);
  }
}

async function fetchPublicCatalog(): Promise<{
  catalogRevision: number;
  games: CatalogGame[];
}> {
  const response = await fetch(`${apiBase}/api/v1/catalog`, { cache: 'no-cache' });
  if (!response.ok) throw await apiFailure(response);
  return (await response.json()) as {
    catalogRevision: number;
    games: CatalogGame[];
  };
}

function currentPrivateSession(): { sessionId: string; token: string | null } | null {
  const url = new URL(window.location.href);
  const sessionId = url.searchParams.get('testSession');
  if (!sessionId) return null;

  const fragment = new URLSearchParams(url.hash.replace(/^#/, ''));
  const fragmentToken = fragment.get('token');
  const storageKey = `retro-arcade-test-token:${sessionId}`;

  if (fragmentToken) {
    sessionStorage.setItem(storageKey, fragmentToken);
    url.hash = '';
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  }

  return {
    sessionId,
    token: fragmentToken ?? sessionStorage.getItem(storageKey),
  };
}

export function App() {
  const runtimeMountRef = useRef<HTMLDivElement>(null);
  const adapterRef = useRef<EmulatorJsAdapter | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const persistenceBusyRef = useRef(false);
  const embedBridgeRef = useRef<ReturnType<typeof createPlayerEmbedBridge> | null>(null);
  const embedCommandHandlerRef = useRef<((message: EmbedHostMessage) => Promise<void>) | null>(
    null,
  );
  const embedMessageSequenceRef = useRef(0);

  const [runtimeSnapshot, setRuntimeSnapshot] =
    useState<EmulatorRuntimeSnapshot>(initialRuntimeSnapshot);
  const [capabilities, setCapabilities] = useState<EmulatorCapabilities | null>(null);
  const [statusMessage, setStatusMessage] = useState('Loading published catalog…');
  const [isLaunching, setIsLaunching] = useState(false);
  const [isControlling, setIsControlling] = useState(false);
  const [volumeDraft, setVolumeDraft] = useState(1);
  const [catalog, setCatalog] = useState<CatalogGame[]>([]);
  const [catalogRevision, setCatalogRevision] = useState<number | null>(null);
  const [selectedGame, setSelectedGame] = useState<CatalogGame | null>(null);
  const [activeLaunch, setActiveLaunch] = useState<LaunchDescriptor | null>(null);
  const [nativeSave, setNativeSave] = useState<StoredNativeSave | null>(null);
  const [saveStates, setSaveStates] = useState<StoredSaveState[]>([]);
  const [saveStateSlot, setSaveStateSlot] = useState(1);
  const [isPersisting, setIsPersisting] = useState(false);
  const [inputProfile, setInputProfile] = useState<GbaInputProfile>(() =>
    cloneGbaInputProfile(defaultGbaInputProfile),
  );
  const [inputProfileSavedAt, setInputProfileSavedAt] = useState<string | null>(null);
  const [cheatEntries, setCheatEntries] = useState<CheatEntry[]>([]);
  const [cheatDescription, setCheatDescription] = useState('');
  const [cheatCode, setCheatCode] = useState('');
  const [cheatProfileSavedAt, setCheatProfileSavedAt] = useState<string | null>(null);
  const [romCacheSummary, setRomCacheSummary] = useState<RomCacheSummary | null>(null);
  const [playerStorageSummary, setPlayerStorageSummary] = useState<PlayerStorageSummary | null>(
    null,
  );
  const [browserStorageEstimate, setBrowserStorageEstimate] =
    useState<BrowserStorageEstimate | null>(null);
  const [isInspectingStorage, setIsInspectingStorage] = useState(false);
  const [isClearingRomCache, setIsClearingRomCache] = useState(false);
  const [embedBootstrap] = useState(() => parsePlayerEmbedBootstrap(window.location.href));
  const [embedView, setEmbedView] = useState<'library' | 'runtime'>('library');
  const [privateSession] = useState<{
    sessionId: string;
    token: string | null;
  } | null>(() => (embedBootstrap ? null : currentPrivateSession()));

  function emitEmbedEvent<TType extends EmbedPlayerEventType>(
    type: TType,
    payload: EmbedPlayerPayloadMap[TType],
  ) {
    const bridge = embedBridgeRef.current;
    if (!bridge || !embedBootstrap) return;

    embedMessageSequenceRef.current += 1;
    bridge.send(
      createPlayerMessage(
        embedBootstrap.channelId,
        `player-${embedMessageSequenceRef.current}`,
        type,
        payload,
      ),
    );
  }

  useEffect(() => {
    void persistenceStore
      .getGbaInputProfile(emulatorJsGbaRuntime.runtimeId)
      .then((stored) => {
        if (!stored) return;
        setInputProfile(cloneGbaInputProfile(stored.profile));
        setInputProfileSavedAt(stored.updatedAt);
      })
      .catch(() => {
        // Input preferences are optional; runtime defaults remain available.
      });
  }, []);

  useEffect(() => {
    if (!embedBootstrap) void refreshStorageManager();
  }, [embedBootstrap]);

  useEffect(() => {
    if (!embedBootstrap || window.parent === window) return;

    const bridge = createPlayerEmbedBridge({
      eventTarget: window,
      hostWindow: window.parent,
      expectedHostSource: window.parent,
      hostOrigin: embedBootstrap.hostOrigin,
      channelId: embedBootstrap.channelId,
    });
    embedBridgeRef.current = bridge;
    const unsubscribe = bridge.subscribe((message) => {
      const handler = embedCommandHandlerRef.current;
      if (handler) void handler(message);
    });

    return () => {
      unsubscribe();
      bridge.destroy();
      if (embedBridgeRef.current === bridge) embedBridgeRef.current = null;
    };
  }, [embedBootstrap]);

  useEffect(() => {
    const mount = runtimeMountRef.current;
    if (!mount) return;

    const host = new BrowserEmulatorJsRuntimeHost(mount);
    const adapter = new EmulatorJsAdapter(host);
    const unsubscribe = adapter.subscribe((event) => {
      if (event.type === 'state-changed') setRuntimeSnapshot(event.snapshot);
      if (event.type === 'runtime-error') setRuntimeSnapshot(event.snapshot);
    });

    adapterRef.current = adapter;
    setRuntimeSnapshot(adapter.snapshot());
    setCapabilities(adapter.capabilities());

    return () => {
      unsubscribe();
      adapter.dispose();
      adapterRef.current = null;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    };
  }, []);

  async function launchDescriptor(descriptor: LaunchDescriptor): Promise<boolean> {
    const adapter = adapterRef.current;
    if (!adapter || isLaunching) return false;

    if (
      descriptor.game.platform !== 'gba' ||
      descriptor.runtime.runtimeId !== emulatorJsGbaRuntime.runtimeId ||
      descriptor.runtime.core !== 'mgba' ||
      descriptor.runtime.emulatorVersion !== emulatorJsGbaRuntime.release.version
    ) {
      setStatusMessage('Launch descriptor does not match the pinned validated GBA runtime.');
      return false;
    }

    setIsLaunching(true);
    setStatusMessage(
      descriptor.access === 'PRIVATE_TEST'
        ? 'Downloading and verifying the private compatibility ROM…'
        : 'Loading the publication-scoped ROM cache…',
    );

    try {
      const loadedBinary = await loadRomBinary({
        access: descriptor.access,
        catalogRevision: descriptor.catalogRevision,
        runtimeId: descriptor.runtime.runtimeId,
        sha256: descriptor.variant.sha256,
        sizeBytes: descriptor.variant.sizeBytes,
        url: descriptor.binary.url,
      });

      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const objectUrl = URL.createObjectURL(loadedBinary.blob);
      objectUrlRef.current = objectUrl;

      setStatusMessage(
        loadedBinary.source === 'cache'
          ? 'Verified ROM loaded from the current publication cache. Starting runtime…'
          : 'ROM downloaded and SHA-256 verified. Starting runtime…',
      );

      const state = adapter.snapshot().state;
      if (state === 'ready' || state === 'paused' || state === 'error') {
        await adapter.terminate();
      }

      const snapshot = await adapter.launch({
        gameId: descriptor.game.id,
        variantId: descriptor.variant.id,
        displayName: descriptor.game.title,
        platform: 'GBA',
        romSha256: descriptor.variant.sha256,
        romUrl: objectUrl,
        inputProfile:
          descriptor.access === 'PUBLIC'
            ? cloneGbaInputProfile(inputProfile)
            : cloneGbaInputProfile(defaultGbaInputProfile),
      });

      setActiveLaunch(descriptor);
      setCatalogRevision(descriptor.catalogRevision);
      setNativeSave(null);
      setSaveStates([]);
      setCheatEntries([]);
      setCheatProfileSavedAt(null);

      if (snapshot.state === 'ready') {
        if (descriptor.access === 'PUBLIC') {
          try {
            const identity = persistenceIdentityForLaunch(descriptor);
            const [stored, storedStates, storedCheats] = await Promise.all([
              persistenceStore.getNativeSave(identity),
              persistenceStore.listSaveStates(identity),
              persistenceStore.getCheatProfile(identity),
            ]);
            setNativeSave(stored);
            setSaveStates(storedStates);
            setCheatEntries(storedCheats ? [...storedCheats.entries] : []);
            setCheatProfileSavedAt(storedCheats?.updatedAt ?? null);
            await applyCheatEntriesToAdapter(adapter, storedCheats?.entries ?? []);
            if (stored) {
              await adapter.importNativeSave(new Uint8Array(stored.data));
              setStatusMessage(
                `Published game started. Restored local native save from ${new Date(
                  stored.updatedAt,
                ).toLocaleString()}.`,
              );
            } else {
              setStatusMessage(
                'Published game started through the validated mGBA runtime. No local native save exists yet.',
              );
            }
          } catch {
            setStatusMessage(
              'Published game started, but local native-save restore was unavailable in this browser.',
            );
          }
        } else {
          setStatusMessage(
            'Private compatibility game started. Local persistence is isolated from owner test sessions.',
          );
        }
        setVolumeDraft(snapshot.volume);
        return true;
      }

      if (snapshot.state === 'error') {
        setStatusMessage(snapshot.error?.message ?? 'The emulator runtime failed to start.');
        URL.revokeObjectURL(objectUrl);
        if (objectUrlRef.current === objectUrl) objectUrlRef.current = null;
      }
      return false;
    } catch {
      setStatusMessage('Launch failed before the emulator reached READY.');
      return false;
    } finally {
      setIsLaunching(false);
    }
  }

  async function loadCatalog() {
    setStatusMessage('Loading published catalog…');
    try {
      const payload = await fetchPublicCatalog();
      setCatalog(payload.games);
      setCatalogRevision(payload.catalogRevision);
      setSelectedGame((current) => {
        if (current) {
          return payload.games.find((game) => game.id === current.id) ?? payload.games[0] ?? null;
        }
        return payload.games[0] ?? null;
      });
      setStatusMessage(
        payload.games.length
          ? `Loaded ${payload.games.length} published game(s).`
          : 'No games are published yet.',
      );
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : 'Unable to load catalog.');
    }
  }

  async function requestPublicLaunch(game: CatalogGame): Promise<boolean> {
    setStatusMessage(`Requesting launch authorization for ${game.title}…`);
    try {
      const response = await fetch(
        `${apiBase}/api/v1/games/${encodeURIComponent(game.id)}/launch`,
        { cache: 'no-store' },
      );
      if (!response.ok) throw await apiFailure(response);
      return await launchDescriptor((await response.json()) as LaunchDescriptor);
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : 'Published launch failed.');
      return false;
    }
  }

  async function loadEmbedGame(identifier: string): Promise<boolean> {
    if (!embedBootstrap || privateSession) return false;

    try {
      let games = catalog;
      let game = resolveEmbedCatalogGame(games, identifier);

      if (!game) {
        const payload = await fetchPublicCatalog();
        games = payload.games;
        setCatalog(games);
        setCatalogRevision(payload.catalogRevision);
        game = resolveEmbedCatalogGame(games, identifier);
      }

      if (!game) {
        setStatusMessage(`Published game "${identifier}" was not found.`);
        return false;
      }

      setSelectedGame(game);
      setEmbedView('runtime');
      return await requestPublicLaunch(game);
    } catch (error) {
      setStatusMessage(error instanceof Error ? error.message : 'Embed game launch failed.');
      return false;
    }
  }

  async function launchEmbedCatalogGame(game: CatalogGame): Promise<void> {
    if (!embedBootstrap || isLaunching) return;
    setSelectedGame(game);
    setEmbedView('runtime');
    await requestPublicLaunch(game);
  }

  async function returnToEmbedLibrary(): Promise<void> {
    if (!embedBootstrap || isLaunching) return;

    await persistNativeSave(false);

    const adapter = adapterRef.current;
    if (adapter) {
      const state = adapter.snapshot().state;
      if (state === 'loading' || state === 'ready' || state === 'paused' || state === 'error') {
        const snapshot = await adapter.terminate();
        setRuntimeSnapshot(snapshot);
      }
    }

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    setActiveLaunch(null);
    setNativeSave(null);
    setSaveStates([]);
    setCheatEntries([]);
    setCheatProfileSavedAt(null);
    setEmbedView('library');
    setStatusMessage(
      catalog.length ? 'Choose a published game.' : 'No games are currently published.',
    );
  }

  async function requestPrivateLaunch(session: { sessionId: string; token: string | null }) {
    if (!session.token) {
      setStatusMessage(
        'Private test token is unavailable in this tab. Return to Admin and create a new session after expiry.',
      );
      return;
    }

    setStatusMessage('Authorizing private compatibility session…');
    try {
      const response = await fetch(
        `${apiBase}/api/v1/test-sessions/${encodeURIComponent(session.sessionId)}/launch`,
        {
          method: 'POST',
          cache: 'no-store',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ token: session.token }),
        },
      );
      if (!response.ok) throw await apiFailure(response);
      await launchDescriptor((await response.json()) as LaunchDescriptor);
    } catch (error) {
      setStatusMessage(
        error instanceof Error ? error.message : 'Private compatibility launch failed.',
      );
    }
  }

  useEffect(() => {
    if (privateSession) {
      void requestPrivateLaunch(privateSession);
    } else {
      void loadCatalog();
    }
  }, []);

  useEffect(() => {
    if (privateSession) return;

    const revalidateCatalog = async () => {
      try {
        const response = await fetch(`${apiBase}/api/v1/catalog/version`, {
          cache: 'no-cache',
        });
        if (!response.ok) return;
        const payload = (await response.json()) as {
          catalogState: { revision: number };
        };
        if (catalogRevision !== null && payload.catalogState.revision !== catalogRevision) {
          await loadCatalog();
        }
      } catch {
        // Focus revalidation is opportunistic; launch authorization still fails closed server-side.
      }
    };

    window.addEventListener('focus', revalidateCatalog);
    return () => window.removeEventListener('focus', revalidateCatalog);
  }, [catalogRevision, privateSession]);

  async function refreshStorageManager(): Promise<void> {
    if (isInspectingStorage) return;

    setIsInspectingStorage(true);
    try {
      const estimatePromise =
        typeof navigator.storage?.estimate === 'function'
          ? navigator.storage.estimate()
          : Promise.resolve({ usage: undefined, quota: undefined });

      const [cacheSummary, persistenceSummary, estimate] = await Promise.all([
        inspectRomCache(),
        persistenceStore.inspectStorage(),
        estimatePromise,
      ]);

      setRomCacheSummary(cacheSummary);
      setPlayerStorageSummary(persistenceSummary);
      setBrowserStorageEstimate({
        usageBytes: typeof estimate.usage === 'number' ? estimate.usage : null,
        quotaBytes: typeof estimate.quota === 'number' ? estimate.quota : null,
      });
    } catch {
      setStatusMessage('Local storage usage could not be inspected in this browser.');
    } finally {
      setIsInspectingStorage(false);
    }
  }

  async function clearRomCacheFromStorageManager(): Promise<void> {
    if (
      !window.confirm(
        'Clear only the verified ROM cache? Native saves, save states, input mappings and cheats will remain intact.',
      )
    ) {
      return;
    }

    setIsClearingRomCache(true);
    try {
      await clearRomCache();
      await refreshStorageManager();
      setStatusMessage(
        'ROM cache cleared. User saves, save states, input mappings and cheat profiles were not modified.',
      );
    } catch {
      setStatusMessage('ROM cache could not be cleared in this browser.');
    } finally {
      setIsClearingRomCache(false);
    }
  }

  function updateKeyboardBinding(action: GbaInputAction, value: EmulatorJsKeyboardKey): void {
    setInputProfile((current) => ({
      ...current,
      keyboard: { ...current.keyboard, [action]: value },
    }));
  }

  function updateGamepadBinding(action: GbaInputAction, value: EmulatorJsGamepadBinding): void {
    setInputProfile((current) => ({
      ...current,
      gamepad: { ...current.gamepad, [action]: value },
    }));
  }

  async function saveInputProfile(): Promise<void> {
    try {
      const stored = await persistenceStore.putGbaInputProfile(
        emulatorJsGbaRuntime.runtimeId,
        inputProfile,
      );
      setInputProfileSavedAt(stored.updatedAt);
      setStatusMessage(
        'GBA input profile saved. Press Restart to rebuild the runtime with the new mapping.',
      );
    } catch {
      setStatusMessage('Input profile is invalid or could not be stored.');
    }
  }

  async function resetInputProfile(): Promise<void> {
    try {
      await persistenceStore.deleteGbaInputProfile(emulatorJsGbaRuntime.runtimeId);
      setInputProfile(cloneGbaInputProfile(defaultGbaInputProfile));
      setInputProfileSavedAt(null);
      setStatusMessage(
        'GBA input profile reset. Press Restart to rebuild the runtime with the default mapping.',
      );
    } catch {
      setStatusMessage('Input profile could not be reset.');
    }
  }

  async function applyCheatEntriesToAdapter(
    adapter: EmulatorJsAdapter,
    entries: readonly CheatEntry[],
  ): Promise<void> {
    await adapter.resetCheats();
    for (let index = 0; index < entries.length; index += 1) {
      const entry = entries[index]!;
      if (entry.enabled) await adapter.setCheat(index, true, entry.code);
    }
  }

  async function commitCheatEntries(
    entries: readonly CheatEntry[],
    successMessage: string,
  ): Promise<void> {
    const adapter = adapterRef.current;
    const descriptor = activeLaunch;
    if (
      !adapter ||
      !descriptor ||
      descriptor.access !== 'PUBLIC' ||
      capabilities?.cheats !== true ||
      persistenceBusyRef.current
    ) {
      return;
    }

    persistenceBusyRef.current = true;
    setIsPersisting(true);
    try {
      await applyCheatEntriesToAdapter(adapter, entries);
      if (entries.length === 0) {
        await persistenceStore.deleteCheatProfile(persistenceIdentityForLaunch(descriptor));
        setCheatEntries([]);
        setCheatProfileSavedAt(null);
      } else {
        const stored = await persistenceStore.putCheatProfile(
          persistenceIdentityForLaunch(descriptor),
          entries,
        );
        setCheatEntries([...stored.entries]);
        setCheatProfileSavedAt(stored.updatedAt);
      }
      setStatusMessage(successMessage);
    } catch {
      setStatusMessage('Cheat profile could not be applied or stored.');
    } finally {
      persistenceBusyRef.current = false;
      setIsPersisting(false);
    }
  }

  async function addCheat(): Promise<void> {
    const description = cheatDescription.trim();
    const code = cheatCode.trim();
    if (!description || !code) {
      setStatusMessage('Cheat description and code are required.');
      return;
    }
    if (description.length > 120 || code.length > 4096 || cheatEntries.length >= 64) {
      setStatusMessage('Cheat exceeds the supported description/code/count limits.');
      return;
    }

    const next: CheatEntry[] = [
      ...cheatEntries,
      {
        id: crypto.randomUUID(),
        description,
        code,
        enabled: false,
      },
    ];
    await commitCheatEntries(next, 'Cheat added disabled. Enable it explicitly when needed.');
    setCheatDescription('');
    setCheatCode('');
  }

  async function toggleCheat(id: string, enabled: boolean): Promise<void> {
    const next = cheatEntries.map((entry) => (entry.id === id ? { ...entry, enabled } : entry));
    await commitCheatEntries(
      next,
      enabled
        ? 'Cheat enabled for the active runtime and stored locally.'
        : 'Cheat disabled. Some cheat effects may require a game restart to clear.',
    );
  }

  async function removeCheat(id: string): Promise<void> {
    const entry = cheatEntries.find((candidate) => candidate.id === id);
    if (!entry) return;
    if (!window.confirm(`Remove cheat "${entry.description}" from this ROM profile?`)) return;

    const next = cheatEntries.filter((candidate) => candidate.id !== id);
    await commitCheatEntries(next, 'Cheat removed from the exact ROM profile.');
  }

  async function clearCheats(): Promise<void> {
    if (!cheatEntries.length) return;
    if (!window.confirm('Remove every cheat stored for this exact ROM?')) return;
    await commitCheatEntries([], 'All cheats removed and the runtime cheat table was reset.');
  }

  async function persistNativeSave(announce = true): Promise<void> {
    const adapter = adapterRef.current;
    const descriptor = activeLaunch;
    if (
      !adapter ||
      !descriptor ||
      descriptor.access !== 'PUBLIC' ||
      !adapter.capabilities().nativeSave ||
      persistenceBusyRef.current
    ) {
      return;
    }

    const state = adapter.snapshot().state;
    if (state !== 'ready' && state !== 'paused') return;

    persistenceBusyRef.current = true;
    setIsPersisting(true);
    try {
      const data = await adapter.exportNativeSave();
      if (!data || data.byteLength === 0) {
        if (announce) {
          setStatusMessage(
            'The game has not produced a native save file yet. Save inside the game first.',
          );
        }
        return;
      }

      const stored = await persistenceStore.putNativeSave(
        persistenceIdentityForLaunch(descriptor),
        data,
      );
      setNativeSave(stored);
      if (announce) {
        setStatusMessage(
          `Native save stored locally (${formatBytes(stored.sizeBytes)}). ROM cache and save data remain separate.`,
        );
      }
    } catch {
      if (announce) {
        setStatusMessage('Native save could not be stored locally in this browser.');
      }
    } finally {
      persistenceBusyRef.current = false;
      setIsPersisting(false);
    }
  }

  async function importNativeSaveFile(file: File): Promise<void> {
    const adapter = adapterRef.current;
    const descriptor = activeLaunch;
    if (!adapter || !descriptor || descriptor.access !== 'PUBLIC') return;

    persistenceBusyRef.current = true;
    setIsPersisting(true);
    try {
      const data = new Uint8Array(await file.arrayBuffer());
      const stored = await persistenceStore.putNativeSave(
        persistenceIdentityForLaunch(descriptor),
        data,
      );
      await adapter.importNativeSave(data);
      setNativeSave(stored);
      setStatusMessage(
        `Imported ${file.name} as the native save for this exact ROM/runtime identity.`,
      );
    } catch {
      setStatusMessage(
        'Native-save import failed. The selected file may be empty, too large, or incompatible.',
      );
    } finally {
      persistenceBusyRef.current = false;
      setIsPersisting(false);
    }
  }

  function exportNativeSaveFile(): void {
    const descriptor = activeLaunch;
    if (!descriptor || descriptor.access !== 'PUBLIC' || !nativeSave) return;

    const url = URL.createObjectURL(
      new Blob([nativeSave.data.slice(0)], { type: 'application/octet-stream' }),
    );
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${descriptor.game.slug || descriptor.game.id}.sav`;
    anchor.click();
    URL.revokeObjectURL(url);
    setStatusMessage('Native save exported from Retro Arcade local storage.');
  }

  async function deleteNativeSave(): Promise<void> {
    const descriptor = activeLaunch;
    if (!descriptor || descriptor.access !== 'PUBLIC' || !nativeSave) return;
    if (
      !window.confirm(
        'Delete the local native save for this exact ROM? This does not delete the ROM cache.',
      )
    ) {
      return;
    }

    persistenceBusyRef.current = true;
    setIsPersisting(true);
    try {
      await persistenceStore.deleteNativeSave(persistenceIdentityForLaunch(descriptor));
      setNativeSave(null);
      setStatusMessage(
        'Local native save deleted. ROM cache and other game identities were not modified.',
      );
    } catch {
      setStatusMessage('Local native save could not be deleted.');
    } finally {
      persistenceBusyRef.current = false;
      setIsPersisting(false);
    }
  }

  async function captureSaveState(): Promise<void> {
    const adapter = adapterRef.current;
    const descriptor = activeLaunch;
    if (
      !adapter ||
      !descriptor ||
      descriptor.access !== 'PUBLIC' ||
      capabilities?.saveStates !== true ||
      persistenceBusyRef.current
    ) {
      return;
    }

    persistenceBusyRef.current = true;
    setIsPersisting(true);
    try {
      const data = await adapter.captureSaveState();
      const stored = await persistenceStore.putSaveState(
        persistenceIdentityForLaunch(descriptor),
        saveStateSlot,
        data,
      );
      setSaveStates((current) =>
        [...current.filter((item) => item.slot !== stored.slot), stored].sort(
          (left, right) => left.slot - right.slot,
        ),
      );
      setStatusMessage(
        `Save state captured in slot ${stored.slot} (${formatBytes(stored.sizeBytes)}).`,
      );
    } catch {
      setStatusMessage('Save state could not be captured for this runtime.');
    } finally {
      persistenceBusyRef.current = false;
      setIsPersisting(false);
    }
  }

  async function restoreSaveState(): Promise<void> {
    const adapter = adapterRef.current;
    const descriptor = activeLaunch;
    if (!adapter || !descriptor || descriptor.access !== 'PUBLIC') return;

    const stored = saveStates.find((item) => item.slot === saveStateSlot);
    if (!stored) {
      setStatusMessage(`No save state exists in slot ${saveStateSlot}.`);
      return;
    }

    persistenceBusyRef.current = true;
    setIsPersisting(true);
    try {
      await adapter.restoreSaveState(new Uint8Array(stored.data));
      setStatusMessage(
        `Save state slot ${stored.slot} restored for the exact ROM/runtime identity.`,
      );
    } catch {
      setStatusMessage('Save state restore failed or the runtime rejected the stored state.');
    } finally {
      persistenceBusyRef.current = false;
      setIsPersisting(false);
    }
  }

  async function deleteSaveState(): Promise<void> {
    const descriptor = activeLaunch;
    if (!descriptor || descriptor.access !== 'PUBLIC') return;
    const stored = saveStates.find((item) => item.slot === saveStateSlot);
    if (!stored) return;

    if (!window.confirm(`Delete save-state slot ${saveStateSlot}? Native saves are unaffected.`)) {
      return;
    }

    persistenceBusyRef.current = true;
    setIsPersisting(true);
    try {
      await persistenceStore.deleteSaveState(
        persistenceIdentityForLaunch(descriptor),
        saveStateSlot,
      );
      setSaveStates((current) => current.filter((item) => item.slot !== saveStateSlot));
      setStatusMessage(
        `Save-state slot ${saveStateSlot} deleted. Native save and ROM cache were not modified.`,
      );
    } catch {
      setStatusMessage('Save-state slot could not be deleted.');
    } finally {
      persistenceBusyRef.current = false;
      setIsPersisting(false);
    }
  }

  const applyRuntimeControl = async (
    action: (adapter: EmulatorJsAdapter) => Promise<EmulatorRuntimeSnapshot>,
    successMessage: string,
  ): Promise<boolean> => {
    const adapter = adapterRef.current;
    if (!adapter || isControlling) return false;

    setIsControlling(true);
    try {
      const snapshot = await action(adapter);
      setRuntimeSnapshot(snapshot);
      setStatusMessage(successMessage);
      return true;
    } catch {
      setStatusMessage('The runtime control could not be applied. Gameplay state was preserved.');
      return false;
    } finally {
      setIsControlling(false);
    }
  };

  const pauseRuntime = async (): Promise<boolean> => {
    if (runtimeSnapshot.state !== 'ready') return false;
    const applied = await applyRuntimeControl((adapter) => adapter.pause(), 'Runtime paused.');
    if (applied) await persistNativeSave(false);
    return applied;
  };

  const resumeRuntime = async (): Promise<boolean> => {
    if (runtimeSnapshot.state !== 'paused') return false;
    return applyRuntimeControl(
      (adapter) => adapter.resume(),
      'Runtime resumed. Keyboard and gamepad input are active.',
    );
  };

  const togglePause = async () => {
    if (runtimeSnapshot.state === 'paused') {
      await resumeRuntime();
      return;
    }
    await pauseRuntime();
  };

  const restartRuntime = async (): Promise<boolean> => {
    const adapter = adapterRef.current;
    const descriptor = activeLaunch;
    const objectUrl = objectUrlRef.current;
    if (!adapter || !descriptor || !objectUrl || isControlling || isLaunching) return false;

    await persistNativeSave(false);
    setIsControlling(true);
    setStatusMessage('Rebuilding the runtime with the current input profile…');

    try {
      const state = adapter.snapshot().state;
      if (state === 'ready' || state === 'paused' || state === 'error') {
        await adapter.terminate();
      }

      let snapshot = await adapter.launch({
        gameId: descriptor.game.id,
        variantId: descriptor.variant.id,
        displayName: descriptor.game.title,
        platform: 'GBA',
        romSha256: descriptor.variant.sha256,
        romUrl: objectUrl,
        inputProfile:
          descriptor.access === 'PUBLIC'
            ? cloneGbaInputProfile(inputProfile)
            : cloneGbaInputProfile(defaultGbaInputProfile),
      });

      if (snapshot.state !== 'ready') {
        setRuntimeSnapshot(snapshot);
        setStatusMessage(snapshot.error?.message ?? 'The emulator runtime failed to restart.');
        return false;
      }

      if (descriptor.access === 'PUBLIC') {
        const identity = persistenceIdentityForLaunch(descriptor);
        const stored = await persistenceStore.getNativeSave(identity);
        if (stored) {
          await adapter.importNativeSave(new Uint8Array(stored.data));
          setNativeSave(stored);
        }
        await applyCheatEntriesToAdapter(adapter, cheatEntries);
      }

      snapshot = await adapter.setVolume(volumeDraft);
      setRuntimeSnapshot(snapshot);
      setStatusMessage(
        descriptor.access === 'PUBLIC'
          ? 'Game restarted with the current input profile. Local save and cheats were restored.'
          : 'Private compatibility game restarted with the pinned default input profile.',
      );
      return true;
    } catch {
      setStatusMessage('Runtime restart failed. Persisted user data was preserved.');
      return false;
    } finally {
      setIsControlling(false);
    }
  };

  const applyRuntimeVolume = async (volume: number): Promise<boolean> => {
    setVolumeDraft(volume);
    return applyRuntimeControl(
      (adapter) => adapter.setVolume(volume),
      `Runtime volume set to ${Math.round(volume * 100)}%.`,
    );
  };

  const applyVolume = async () => {
    await applyRuntimeVolume(volumeDraft);
  };

  const requestFullscreen = async (): Promise<boolean> =>
    applyRuntimeControl(
      (adapter) => adapter.requestFullscreen(),
      'Fullscreen requested for the emulator frame.',
    );

  embedCommandHandlerRef.current = async (message) => {
    await handlePlayerEmbedCommand(
      message,
      {
        loadGame: loadEmbedGame,
        pause: pauseRuntime,
        resume: resumeRuntime,
        restart: restartRuntime,
        setVolume: applyRuntimeVolume,
        requestFullscreen,
      },
      emitEmbedEvent,
    );
  };

  const runtimeState = runtimeSnapshot.state;
  const runtimeActive = runtimeState === 'ready' || runtimeState === 'paused';
  const runtimeVisible = runtimeState === 'loading' || runtimeActive;

  useEffect(() => {
    if (!embedBootstrap) return;
    emitEmbedEvent('RUNTIME_STATE', {
      state: runtimeState,
      gameId: activeLaunch?.game.id ?? null,
    });
  }, [embedBootstrap, runtimeState, activeLaunch?.game.id]);

  useEffect(() => {
    if (!embedBootstrap || !activeLaunch) return;
    emitEmbedEvent('GAME_CHANGED', {
      gameId: activeLaunch.game.id,
      slug: activeLaunch.game.slug,
      title: activeLaunch.game.title,
    });
  }, [embedBootstrap, activeLaunch?.game.id]);

  useEffect(() => {
    if (!embedBootstrap || !runtimeSnapshot.error) return;
    emitEmbedEvent('PLAYER_ERROR', {
      code: runtimeSnapshot.error.code,
      message: runtimeSnapshot.error.message,
    });
  }, [embedBootstrap, runtimeSnapshot.error?.code, runtimeSnapshot.error?.message]);

  useEffect(() => {
    if (!runtimeActive || activeLaunch?.access !== 'PUBLIC') return;

    const intervalId = window.setInterval(() => {
      void persistNativeSave(false);
    }, 30_000);

    const persistWhenHidden = () => {
      if (document.visibilityState === 'hidden') void persistNativeSave(false);
    };

    document.addEventListener('visibilitychange', persistWhenHidden);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', persistWhenHidden);
    };
  }, [runtimeActive, activeLaunch?.variant.id, activeLaunch?.access]);

  if (embedBootstrap) {
    const embeddedRuntimeVisible = embedView === 'runtime';

    return (
      <main className="embed-shell">
        {embedView === 'library' ? (
          <section className="embed-library" aria-labelledby="embed-library-title">
            <div className="embed-library__header">
              <div>
                <p className="section-label">Retro Arcade</p>
                <h1 id="embed-library-title">Choose a game</h1>
                <p>
                  Published games run inside Retro Arcade with saves, controls and runtime state
                  managed by the Player.
                </p>
              </div>
              <button
                className="embed-secondary-button"
                type="button"
                disabled={isLaunching}
                onClick={() => void loadCatalog()}
              >
                Refresh
              </button>
            </div>

            <div className="embed-catalog-grid">
              {catalog.length ? (
                catalog.map((game) => (
                  <button
                    className="embed-game-card"
                    type="button"
                    key={game.id}
                    disabled={isLaunching}
                    onClick={() => void launchEmbedCatalogGame(game)}
                  >
                    <span className="embed-game-card__platform">{game.platform.toUpperCase()}</span>
                    <strong>{game.title}</strong>
                    <span>{formatBytes(game.variant.sizeBytes)}</span>
                    {game.summary ? <p>{game.summary}</p> : null}
                    <span className="embed-game-card__action">
                      {isLaunching && selectedGame?.id === game.id ? 'Starting…' : 'Play'}
                    </span>
                  </button>
                ))
              ) : (
                <div className="embed-empty-state">
                  <strong>No published games</strong>
                  <span>{statusMessage}</span>
                </div>
              )}
            </div>

            <p className="embed-status" role="status" aria-live="polite">
              {statusMessage}
            </p>
          </section>
        ) : null}

        <section
          className={
            embeddedRuntimeVisible
              ? 'embed-runtime-view'
              : 'embed-runtime-view embed-runtime-view--parked'
          }
          aria-hidden={!embeddedRuntimeVisible}
        >
          <header className="embed-runtime-toolbar">
            <button
              className="embed-secondary-button"
              type="button"
              disabled={isLaunching}
              onClick={() => void returnToEmbedLibrary()}
            >
              ← Library
            </button>
            <div>
              <p className="section-label">Now playing</p>
              <strong>{activeLaunch?.game.title ?? selectedGame?.title ?? 'Starting game…'}</strong>
            </div>
            <span className={`runtime-badge runtime-badge--${runtimeState}`}>{runtimeState}</span>
          </header>

          <div className="embed-runtime-stage runtime-stage">
            <div ref={runtimeMountRef} className="runtime-mount" />
            {!runtimeVisible ? (
              <div className="runtime-placeholder">
                <strong>{runtimeState === 'error' ? 'Runtime error' : 'Preparing game'}</strong>
                <span>
                  {runtimeState === 'error' ? runtimeSnapshot.error?.message : statusMessage}
                </span>
              </div>
            ) : null}
            {runtimeState === 'loading' ? (
              <div className="runtime-loading" role="status">
                Loading controlled ROM + EmulatorJS/mGBA…
              </div>
            ) : null}
            {runtimeState === 'paused' ? (
              <div className="runtime-paused" role="status">
                Paused
              </div>
            ) : null}
          </div>

          <div className="embed-runtime-controls" aria-label="Embedded game controls">
            <button
              className="embed-control-button"
              type="button"
              disabled={!runtimeActive || isControlling}
              onClick={() => void togglePause()}
            >
              {runtimeState === 'paused' ? 'Resume' : 'Pause'}
            </button>
            <button
              className="embed-control-button"
              type="button"
              disabled={!runtimeActive || isControlling}
              onClick={() => void restartRuntime()}
            >
              Restart
            </button>
            <button
              className="embed-control-button"
              type="button"
              disabled={!runtimeActive || isControlling}
              onClick={() => void requestFullscreen()}
            >
              Fullscreen
            </button>
            <label className="embed-volume-control">
              <span>Volume {Math.round(volumeDraft * 100)}%</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volumeDraft}
                disabled={!runtimeActive || isControlling}
                onChange={(event) => setVolumeDraft(Number(event.target.value))}
                onPointerUp={() => void applyRuntimeVolume(volumeDraft)}
                onKeyUp={() => void applyRuntimeVolume(volumeDraft)}
              />
            </label>
          </div>

          <p className="embed-status" role="status" aria-live="polite">
            {statusMessage}
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="player-shell">
      <header className="player-header">
        <div>
          <p className="eyebrow">{playerAppMeta.milestone} · Embed-ready runtime</p>
          <h1>{playerAppMeta.name}</h1>
          <p className="lede">
            Published gameplay stays behind Retro Arcade-owned catalog, persistence and versioned
            host-integration boundaries around the validated EmulatorJS 4.2.3 + mGBA runtime.
          </p>
        </div>
        <span className={`runtime-badge runtime-badge--${runtimeState}`}>{runtimeState}</span>
      </header>

      <div className="player-grid">
        <aside className="control-panel" aria-label="Game launch controls">
          {privateSession ? (
            <section className="control-section private-session">
              <p className="section-label">Private owner validation</p>
              <strong>Compatibility test session</strong>
              <code>{privateSession.sessionId}</code>
              <p className="helper-text">
                This game is not public. The capability token stays in this browser tab session and
                authorizes only this temporary test flow.
              </p>
              <button
                className="launch-button"
                type="button"
                disabled={isLaunching || !privateSession.token}
                onClick={() => void requestPrivateLaunch(privateSession)}
              >
                {isLaunching ? 'Starting private test…' : 'Retry private test'}
              </button>
            </section>
          ) : (
            <>
              <section className="control-section">
                <div className="catalog-heading">
                  <div>
                    <p className="section-label">Published catalog</p>
                    <strong>Revision {catalogRevision ?? '—'}</strong>
                  </div>
                  <button
                    className="runtime-control-button"
                    type="button"
                    onClick={() => void loadCatalog()}
                  >
                    Refresh
                  </button>
                </div>
                <div className="catalog-list">
                  {catalog.length ? (
                    catalog.map((game) => (
                      <button
                        className={
                          selectedGame?.id === game.id
                            ? 'catalog-game catalog-game--selected'
                            : 'catalog-game'
                        }
                        type="button"
                        key={game.id}
                        onClick={() => setSelectedGame(game)}
                      >
                        <strong>{game.title}</strong>
                        <span>
                          {game.platform.toUpperCase()} · {formatBytes(game.variant.sizeBytes)}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p className="empty-copy">No published games.</p>
                  )}
                </div>
              </section>

              <section className="control-section">
                <p className="section-label">Selected game</p>
                {selectedGame ? (
                  <>
                    <dl className="runtime-facts">
                      <div>
                        <dt>Title</dt>
                        <dd>{selectedGame.title}</dd>
                      </div>
                      <div>
                        <dt>Platform</dt>
                        <dd>{selectedGame.platform.toUpperCase()}</dd>
                      </div>
                      <div>
                        <dt>Runtime</dt>
                        <dd>{selectedGame.runtime.runtimeId}</dd>
                      </div>
                      <div>
                        <dt>Variant</dt>
                        <dd>{selectedGame.variant.id}</dd>
                      </div>
                      <div className="fact-wide">
                        <dt>SHA-256</dt>
                        <dd className="digest">{selectedGame.variant.sha256}</dd>
                      </div>
                    </dl>
                    <button
                      className="launch-button launch-button--spaced"
                      type="button"
                      disabled={isLaunching}
                      onClick={() => void requestPublicLaunch(selectedGame)}
                    >
                      {isLaunching ? 'Starting runtime…' : 'Play published game'}
                    </button>
                  </>
                ) : (
                  <p className="empty-copy">Select a published game.</p>
                )}
              </section>
            </>
          )}

          <section className="control-section">
            <p className="section-label">Runtime status</p>
            <p className="status-message" aria-live="polite">
              {statusMessage}
            </p>
            {activeLaunch ? (
              <p className="helper-text">
                Access: <strong>{activeLaunch.access}</strong> · Variant{' '}
                <code>{activeLaunch.variant.id}</code>
              </p>
            ) : null}
          </section>

          <section className="control-section">
            <p className="section-label">Runtime controls</p>
            <div className="runtime-control-grid">
              <button
                className="runtime-control-button"
                type="button"
                disabled={!runtimeActive || isControlling}
                onClick={togglePause}
              >
                {runtimeState === 'paused' ? 'Resume' : 'Pause'}
              </button>
              <button
                className="runtime-control-button"
                type="button"
                disabled={!runtimeActive || isControlling}
                onClick={restartRuntime}
              >
                Restart
              </button>
              <button
                className="runtime-control-button runtime-control-button--wide"
                type="button"
                disabled={!runtimeActive || isControlling}
                onClick={requestFullscreen}
              >
                Fullscreen
              </button>
            </div>

            <div className="volume-control">
              <div className="volume-control__label">
                <label htmlFor="runtime-volume">Volume</label>
                <output htmlFor="runtime-volume">{Math.round(volumeDraft * 100)}%</output>
              </div>
              <input
                id="runtime-volume"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volumeDraft}
                disabled={!runtimeActive || isControlling}
                onChange={(event) => setVolumeDraft(Number(event.target.value))}
              />
              <button
                className="runtime-control-button runtime-control-button--wide"
                type="button"
                disabled={!runtimeActive || isControlling}
                onClick={applyVolume}
              >
                Apply volume
              </button>
            </div>

            <div className="input-capabilities" aria-label="Available input paths">
              <span data-enabled={capabilities?.keyboard === true}>Keyboard</span>
              <span data-enabled={capabilities?.gamepad === true}>Gamepad API</span>
            </div>
          </section>

          <section className="control-section">
            <p className="section-label">GBA input profile</p>
            {privateSession ? (
              <p className="helper-text">
                Owner compatibility sessions use pinned default controls and ignore personal
                remapping.
              </p>
            ) : (
              <>
                <p className="helper-text">
                  Keyboard and gamepad bindings are stored by runtime/control scheme. Save the
                  profile, then press Restart to apply changes without reloading the page.
                </p>
                <div className="input-profile-table" role="group" aria-label="GBA input mapping">
                  {gbaInputActions.map((action) => (
                    <div className="input-profile-row" key={action}>
                      <strong>{action}</strong>
                      <label>
                        <span>Keyboard</span>
                        <select
                          value={inputProfile.keyboard[action]}
                          onChange={(event) =>
                            updateKeyboardBinding(
                              action,
                              event.target.value as EmulatorJsKeyboardKey,
                            )
                          }
                        >
                          {emulatorJsKeyboardKeys.map((key) => (
                            <option
                              key={key}
                              value={key}
                              disabled={
                                key !== inputProfile.keyboard[action] &&
                                gbaInputActions.some(
                                  (other) =>
                                    other !== action && inputProfile.keyboard[other] === key,
                                )
                              }
                            >
                              {inputBindingLabel(key)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>Gamepad</span>
                        <select
                          value={inputProfile.gamepad[action]}
                          onChange={(event) =>
                            updateGamepadBinding(
                              action,
                              event.target.value as EmulatorJsGamepadBinding,
                            )
                          }
                        >
                          {emulatorJsGamepadBindings.map((binding) => (
                            <option
                              key={binding}
                              value={binding}
                              disabled={
                                binding !== inputProfile.gamepad[action] &&
                                gbaInputActions.some(
                                  (other) =>
                                    other !== action && inputProfile.gamepad[other] === binding,
                                )
                              }
                            >
                              {inputBindingLabel(binding)}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  ))}
                </div>
                <p className="helper-text">
                  {inputProfileSavedAt
                    ? `Last saved: ${new Date(inputProfileSavedAt).toLocaleString()}`
                    : 'Using pinned default mapping until a custom profile is saved.'}
                </p>
                <div className="runtime-control-grid">
                  <button
                    className="runtime-control-button"
                    type="button"
                    onClick={() => void saveInputProfile()}
                  >
                    Save input profile
                  </button>
                  <button
                    className="runtime-control-button"
                    type="button"
                    onClick={() => void resetInputProfile()}
                  >
                    Reset defaults
                  </button>
                </div>
              </>
            )}
          </section>

          <section className="control-section">
            <p className="section-label">Cheat manager</p>
            {activeLaunch?.access === 'PUBLIC' ? (
              <>
                <p className="helper-text">
                  Cheats are stored only for this exact ROM/runtime identity. Codes are sent as
                  bounded text to the pinned core; no executable script is accepted.
                </p>
                <label className="cheat-field">
                  <span>Description</span>
                  <input
                    type="text"
                    maxLength={120}
                    value={cheatDescription}
                    disabled={!runtimeActive || isPersisting || capabilities?.cheats !== true}
                    onChange={(event) => setCheatDescription(event.target.value)}
                    placeholder="e.g. Test code"
                  />
                </label>
                <label className="cheat-field">
                  <span>Code</span>
                  <textarea
                    rows={3}
                    maxLength={4096}
                    value={cheatCode}
                    disabled={!runtimeActive || isPersisting || capabilities?.cheats !== true}
                    onChange={(event) => setCheatCode(event.target.value)}
                    placeholder="Enter a code supported by the active mGBA/libretro core"
                  />
                </label>
                <button
                  className="runtime-control-button runtime-control-button--wide"
                  type="button"
                  disabled={
                    !runtimeActive ||
                    isPersisting ||
                    capabilities?.cheats !== true ||
                    cheatEntries.length >= 64
                  }
                  onClick={() => void addCheat()}
                >
                  Add cheat
                </button>
                <div className="cheat-list">
                  {cheatEntries.length ? (
                    cheatEntries.map((entry) => (
                      <div className="cheat-row" key={entry.id}>
                        <label>
                          <input
                            type="checkbox"
                            checked={entry.enabled}
                            disabled={!runtimeActive || isPersisting}
                            onChange={(event) => void toggleCheat(entry.id, event.target.checked)}
                          />
                          <span>{entry.description}</span>
                        </label>
                        <code>{entry.code}</code>
                        <button
                          className="runtime-control-button"
                          type="button"
                          disabled={isPersisting}
                          onClick={() => void removeCheat(entry.id)}
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="helper-text">No cheats are stored for this ROM.</p>
                  )}
                </div>
                <p className="helper-text">
                  {cheatProfileSavedAt
                    ? `Profile updated: ${new Date(cheatProfileSavedAt).toLocaleString()}`
                    : 'No cheat profile stored.'}{' '}
                  Some cheats may require a game restart before their effects disappear.
                </p>
                <button
                  className="runtime-control-button runtime-control-button--wide"
                  type="button"
                  disabled={!cheatEntries.length || isPersisting}
                  onClick={() => void clearCheats()}
                >
                  Clear all cheats
                </button>
              </>
            ) : (
              <p className="helper-text">
                Cheats are disabled for owner compatibility sessions so validation always uses a
                clean runtime.
              </p>
            )}
          </section>

          <section className="control-section">
            <p className="section-label">Local native save</p>
            {activeLaunch?.access === 'PUBLIC' ? (
              <>
                <p className="helper-text">
                  Stored separately from the ROM cache and scoped to this runtime + exact ROM
                  SHA-256 + game/variant identity.
                </p>
                <dl className="runtime-facts">
                  <div>
                    <dt>Status</dt>
                    <dd>{nativeSave ? 'Stored locally' : 'No local save yet'}</dd>
                  </div>
                  <div>
                    <dt>Size</dt>
                    <dd>{nativeSave ? formatBytes(nativeSave.sizeBytes) : '—'}</dd>
                  </div>
                  <div className="fact-wide">
                    <dt>Updated</dt>
                    <dd>{nativeSave ? new Date(nativeSave.updatedAt).toLocaleString() : '—'}</dd>
                  </div>
                </dl>
                <div className="runtime-control-grid">
                  <button
                    className="runtime-control-button"
                    type="button"
                    disabled={!runtimeActive || isPersisting || capabilities?.nativeSave !== true}
                    onClick={() => void persistNativeSave()}
                  >
                    {isPersisting ? 'Saving…' : 'Save locally now'}
                  </button>
                  <button
                    className="runtime-control-button"
                    type="button"
                    disabled={!nativeSave || isPersisting}
                    onClick={exportNativeSaveFile}
                  >
                    Export .sav
                  </button>
                </div>
                <label className="native-save-import">
                  <span>Import native save (.sav)</span>
                  <input
                    type="file"
                    accept=".sav,application/octet-stream"
                    disabled={!runtimeActive || isPersisting || capabilities?.nativeSave !== true}
                    onChange={(event) => {
                      const selected = event.currentTarget.files?.[0] ?? null;
                      event.currentTarget.value = '';
                      if (selected) void importNativeSaveFile(selected);
                    }}
                  />
                </label>
                <button
                  className="runtime-control-button runtime-control-button--wide"
                  type="button"
                  disabled={!nativeSave || isPersisting}
                  onClick={() => void deleteNativeSave()}
                >
                  Delete local native save
                </button>
              </>
            ) : (
              <p className="helper-text">
                Private compatibility sessions intentionally do not restore or persist local user
                saves.
              </p>
            )}
          </section>

          <section className="control-section">
            <p className="section-label">Save-state slots</p>
            {activeLaunch?.access === 'PUBLIC' ? (
              <>
                <p className="helper-text">
                  Save states are runtime snapshots, not normal in-game saves. They are scoped to
                  this exact ROM and pinned runtime and can be deleted independently.
                </p>
                <label className="save-state-slot-picker">
                  <span>Active slot</span>
                  <select
                    value={saveStateSlot}
                    disabled={!runtimeActive || isPersisting}
                    onChange={(event) => setSaveStateSlot(Number(event.target.value))}
                  >
                    {Array.from({ length: 9 }, (_, index) => index + 1).map((slot) => (
                      <option key={slot} value={slot}>
                        Slot {slot}
                      </option>
                    ))}
                  </select>
                </label>
                {saveStates.find((item) => item.slot === saveStateSlot) ? (
                  <p className="helper-text">
                    Stored:{' '}
                    {new Date(
                      saveStates.find((item) => item.slot === saveStateSlot)!.updatedAt,
                    ).toLocaleString()}{' '}
                    ·{' '}
                    {formatBytes(saveStates.find((item) => item.slot === saveStateSlot)!.sizeBytes)}
                  </p>
                ) : (
                  <p className="helper-text">This slot is empty.</p>
                )}
                <div className="runtime-control-grid">
                  <button
                    className="runtime-control-button"
                    type="button"
                    disabled={!runtimeActive || isPersisting || capabilities?.saveStates !== true}
                    onClick={() => void captureSaveState()}
                  >
                    Save state
                  </button>
                  <button
                    className="runtime-control-button"
                    type="button"
                    disabled={
                      !runtimeActive ||
                      isPersisting ||
                      !saveStates.some((item) => item.slot === saveStateSlot)
                    }
                    onClick={() => void restoreSaveState()}
                  >
                    Load state
                  </button>
                </div>
                <button
                  className="runtime-control-button runtime-control-button--wide"
                  type="button"
                  disabled={isPersisting || !saveStates.some((item) => item.slot === saveStateSlot)}
                  onClick={() => void deleteSaveState()}
                >
                  Delete selected state
                </button>
              </>
            ) : (
              <p className="helper-text">
                Owner compatibility sessions do not read or write user save-state slots.
              </p>
            )}
          </section>

          <section className="control-section">
            <div className="catalog-heading">
              <div>
                <p className="section-label">Storage manager</p>
                <strong>Separated destructive boundaries</strong>
              </div>
              <button
                className="runtime-control-button"
                type="button"
                disabled={isInspectingStorage}
                onClick={() => void refreshStorageManager()}
              >
                {isInspectingStorage ? 'Inspecting…' : 'Refresh'}
              </button>
            </div>
            <p className="helper-text">
              ROM cache is disposable. Native saves, save states, input mappings and cheats are
              user-owned local data and are managed independently.
            </p>
            <div className="storage-summary-grid">
              <div className="storage-summary-card">
                <strong>ROM cache</strong>
                <span>{formatRomCacheSummary(romCacheSummary)}</span>
              </div>
              <div className="storage-summary-card">
                <strong>Native saves</strong>
                <span>{formatStorageBucket(playerStorageSummary?.nativeSaves)}</span>
              </div>
              <div className="storage-summary-card">
                <strong>Save states</strong>
                <span>{formatStorageBucket(playerStorageSummary?.saveStates)}</span>
              </div>
              <div className="storage-summary-card">
                <strong>Input profiles</strong>
                <span>
                  {playerStorageSummary
                    ? `${playerStorageSummary.inputProfiles.count} profile(s)`
                    : 'Not inspected'}
                </span>
              </div>
              <div className="storage-summary-card">
                <strong>Cheat profiles</strong>
                <span>
                  {playerStorageSummary
                    ? `${playerStorageSummary.cheatProfiles.count} profile(s)`
                    : 'Not inspected'}
                </span>
              </div>
              <div className="storage-summary-card">
                <strong>Browser origin usage</strong>
                <span>{formatBrowserStorageEstimate(browserStorageEstimate)}</span>
              </div>
            </div>
            <div className="storage-actions">
              <button
                className="runtime-control-button runtime-control-button--wide"
                type="button"
                disabled={isClearingRomCache}
                onClick={() => void clearRomCacheFromStorageManager()}
              >
                {isClearingRomCache ? 'Clearing ROM cache…' : 'Clear ROM cache only'}
              </button>
            </div>
            <p className="helper-text">
              Per-game native saves and save states can be deleted in their sections above. Input
              mappings can be reset independently, and cheats can be cleared per exact ROM.
            </p>
          </section>

          <section className="control-section runtime-contract">
            <p className="section-label">Pinned runtime</p>
            <dl className="runtime-facts">
              <div>
                <dt>EmulatorJS</dt>
                <dd>{emulatorJsGbaRuntime.release.version}</dd>
              </div>
              <div>
                <dt>Core</dt>
                <dd>{emulatorJsGbaRuntime.core.emulatorJsCore}</dd>
              </div>
              <div>
                <dt>Threads</dt>
                <dd>Off</dd>
              </div>
              <div>
                <dt>Runtime ID</dt>
                <dd>{emulatorJsGbaRuntime.runtimeId}</dd>
              </div>
            </dl>
          </section>
        </aside>

        <section className="runtime-panel" aria-labelledby="runtime-title">
          <div className="runtime-toolbar">
            <div>
              <p className="section-label">Runtime surface</p>
              <h2 id="runtime-title">{activeLaunch ? activeLaunch.game.title : 'mGBA / GBA'}</h2>
            </div>
            <span className="sequence-label">event #{runtimeSnapshot.sequence}</span>
          </div>

          <div className="runtime-stage">
            <div ref={runtimeMountRef} className="runtime-mount" />
            {!runtimeVisible ? (
              <div className="runtime-placeholder">
                <strong>{runtimeState === 'error' ? 'Runtime error' : 'Waiting for launch'}</strong>
                <span>
                  {runtimeState === 'error'
                    ? runtimeSnapshot.error?.message
                    : privateSession
                      ? 'Private compatibility session is ready to authorize.'
                      : 'Select a published game and request controlled launch.'}
                </span>
              </div>
            ) : null}
            {runtimeState === 'loading' ? (
              <div className="runtime-loading" role="status">
                Loading controlled ROM + EmulatorJS/mGBA…
              </div>
            ) : null}
            {runtimeState === 'paused' ? (
              <div className="runtime-paused" role="status">
                Paused
              </div>
            ) : null}
          </div>

          {runtimeSnapshot.error ? (
            <div className="runtime-error-card" role="alert">
              <div>
                <p className="section-label">Runtime diagnostic</p>
                <strong>{runtimeSnapshot.error.code}</strong>
                <p>{runtimeSnapshot.error.message}</p>
              </div>
              <dl>
                <div>
                  <dt>Reason</dt>
                  <dd>{runtimeSnapshot.error.context?.reason ?? 'UNSPECIFIED'}</dd>
                </div>
                <div>
                  <dt>Recoverable</dt>
                  <dd>{runtimeSnapshot.error.recoverable ? 'Yes' : 'No'}</dd>
                </div>
              </dl>
            </div>
          ) : null}

          <footer className="runtime-footer">
            M6 keeps EmulatorJS private behind the Player while external hosts communicate only
            through the versioned Retro Arcade embed protocol.
          </footer>
        </section>
      </div>
    </main>
  );
}
